/** tm/types.ts — 전체 타입 정의 (단일 파일, 순환참조 없음) */

// ═══ 공용 ═══

export interface UploadedFile {
  name: string;
  size?: string;
  s3_key?: string;
}

export interface AIQuestion {
  id: string;
  q: string;
  type: 'short' | 'long' | 'radio' | 'checkbox';
  required: boolean;
  options: string[];
  answer: string;
}

export interface BPSection {
  title: string;
  maxLen: number;
  hint: string;
}

// ═══ WM / FP ═══

export interface WM {
  id: string;
  account_id: string;
  name: string;
  company: string;
  phone: string;
  email: string;
  memo: string;
  created_at: string;
  updated_at: string;
  title: string;
  /** 대출 요청 링크 토큰 — 필요한 WM님만(WM 관리 「대출 링크 만들기」 · 2026-10-29) */
  loan_token?: string | null;
}

export interface FP {
  id: string;
  account_id: string;
  referrer_id: string | null;
  name: string;
  phone: string;
  email: string;
  company: string;
  wm_id: string | null;
  wm_name?: string;
  wm_company?: string;
  portal_token: string;
  /** fp 팀장님 · tax 세무사(194 · 2026-10-31 세무사별 관리) */
  role?: 'fp' | 'tax';
  loan_token?: string | null;   // 정책자금 대출 요청 링크(/loan/<token>) — 163
  is_active: boolean;
  memo: string;
  created_at: string;
  updated_at: string;
  title: string;
}

export interface CompanyFP {
  fp_id: string;
  name: string;
  phone: string;
  /** 팀장님이 고객관리에서 둔 구분 — 배양 고객 · 계약 고객(2026-10-26 · 없으면 배양) */
  stage?: 'grow' | 'contract';
}

// ═══ 업체 ═══

/** 업체 세부업종 AI 판정 저장 값 (엔진 govMatch.companyAiOf 와 같은 꼴) */
export interface GovCompanyAi {
  v: number; h: string; at?: string | null; model?: string; conf: 'high' | 'mid' | 'low';
  biz: string[]; subs: string[]; fields: string[]; types: string[]; ev: Record<string, string>; note?: string;
}
/** 연락 받을 대표 — 실제 대표가 있으면 그 사람 · 없으면 명의 대표 (2026-10-18 지도사 「문자 · 진행 안내 받는 사람은 실제 대표」 · backend services/ceoContact 와 같은 규칙)
 *  실제 대표 번호가 비면 명의 대표 번호 · 서류 · 신청서 · 계약에는 쓰지 않는다(늘 명의 대표) */
export function ceoContact(c: { ceo?: string | null; ceo_phone?: string | null; real_ceo?: string | null; real_ceo_phone?: string | null } | null | undefined): { name: string; phone: string; real: boolean } {
  const t = (v: string | null | undefined) => String(v || '').trim();
  const x = c || {}, rn = t(x.real_ceo), rp = t(x.real_ceo_phone);
  if (rn || rp) return { name: rn || t(x.ceo), phone: rp || t(x.ceo_phone), real: true };
  return { name: t(x.ceo), phone: t(x.ceo_phone), real: false };
}
/** 화면에 보이는 대표 — 「이영순」 · 실제 대표가 따로 있으면 「이영순 (실제 김대표)」 */
export function ceoShown(c: { ceo?: string | null; real_ceo?: string | null } | null | undefined): string {
  const n = String((c && c.ceo) || '').trim(), r = String((c && c.real_ceo) || '').trim();
  return r && r !== n ? (n ? `${n} (실제 ${r})` : r) : n;
}
/** 이 회사의 상담인가 — 주 회사 또는 함께 상담한 회사(extra_company_ids · 관계사) (2026-10-18 지도사 「상담 결과는 둘 다 동일하게 보이면 되고」) */
export function inConsult(c: { company_id?: string | null; extra_company_ids?: string[] | string | null }, companyId: string): boolean {
  if (!companyId) return false;
  if (c.company_id === companyId) return true;
  let x: unknown = c.extra_company_ids;
  if (typeof x === 'string') { try { x = JSON.parse(x); } catch { x = []; } }
  return Array.isArray(x) && x.map(String).includes(companyId);
}
/** 업체 담당 직원 한 사람 (2026-10-18) */
export interface CompanyManager { name: string; phone: string; email: string }
/** 담당 직원 더 목록 — jsonb 가 글자로 와도 · 빈 줄 빼고 */
export function extraManagersOf(c: Pick<Company, 'extra_managers'> | null | undefined): CompanyManager[] {
  let x: unknown = c ? c.extra_managers : null;
  if (typeof x === 'string') { try { x = JSON.parse(x); } catch { x = []; } }
  if (!Array.isArray(x)) return [];
  return x.filter((m): m is Record<string, unknown> => !!m && typeof m === 'object')
    .map(m => ({ name: String(m.name || ''), phone: String(m.phone || ''), email: String(m.email || '') }))
    .filter(m => m.name || m.phone || m.email);
}
/** 업체 담당 WM 한 분(공동관리 · 2026-10-26) */
export interface CompanyWM { wm_id: string; name: string; company: string; phone: string }
export interface Company {
  id: string;
  account_id: string;
  name: string;
  biz_no: string;
  biz_type: string;
  biz_types?: string[];
  is_startup?: boolean;
  company_scale?: string;
  region: string;
  ceo: string;
  ceo_phone: string;
  ceo_email: string;
  /** 대표 생년월일 (YYYY-MM-DD) — 청년 대상 지원사업 판정. 나이는 매칭 시점에 계산한다 */
  ceo_birth?: string | null;
  /** 상시근로자수 — 정부지원사업 매칭(연매출·직원수 조건)에 쓰인다 */
  employees?: number | null;
  /** 최근 연매출(억원) — 정부지원사업 매칭에 쓰인다 */
  sales_eok?: number | null;
  manager: string;
  manager_phone: string;
  manager_email: string;
  /** 실제 대표 — 명의 대표(ceo)와 다를 때만(183 · 2026-10-18) */
  real_ceo?: string;
  real_ceo_phone?: string;
  /** 담당 직원 더(첫 담당자는 manager 칸) — jsonb 가 글자로 올 때도 (182 · 2026-10-18) */
  extra_managers?: CompanyManager[] | string | null;
  address: string;
  corp_type: string;
  tags: string[];
  /** 업종으로 자동 해당한 전용 대상 중 «아님»으로 뺀 것 (2026-10-05f · 144 마이그레이션) — JSONB 라 문자열로 올 수도 있다 */
  sectors_off?: string[] | string | null;
  /** 세부업종 AI 판정 (2026-10-07k · 152 마이그레이션 · services/govCompanyAi.js) — 엔진은 h 가 지금 업종 글과 같을 때만 쓴다 · JSONB 라 문자열로 올 수도 있다 */
  gov_ai?: GovCompanyAi | string | null;
  tm_status: '대기' | '진행중' | '완료' | '보류' | '중단';   // 중단 = 업체 포기 (2026-10-12)
  /** 소개 경로 (2026-10-12) — FP · 세무사무소 · 지인 · 직접 문의 / 내용(사무소 · 담당자 · 연락처 · 소개한 분 · 알게 된 곳) */
  referral_type?: 'FP' | '세무사무소' | '지인' | '직접 문의' | null;
  referral_detail?: { office?: string; person?: string; phone?: string; how?: string } | null;
  source: string;
  /** 대표 WM(company_wms 의 첫 분 · 옛 화면 · 보고서 보내기) */
  wm_id: string | null;
  wm_name?: string;
  wm_company?: string;
  /** 담당 WM 여러 명(공동관리 · 2026-10-26 · 204 마이그레이션) — 대표 먼저 */
  wms?: CompanyWM[];
  portal_token: string;
  tm_memo: string;
  established_year: number | null;
  region_detail: string;
  biz_type_detail: string;
  established_date: string;
  company_reg_num: string;
  fps: CompanyFP[];
  created_at: string;
  updated_at: string;
}

// ═══ 업무 단계 extras ═══

export interface CollectExtras {
  collect_ids: string[];
  custom_docs: string[];
  collect_sent: boolean;
  collect_project_id: string | null;
  /** 업무를 만들 때 이을 이미 만든 자료수집(서버가 잇고 지움 · 2026-10-28) */
  link_project_id?: string;
}

export interface AIQuestionnaireExtras {
  ai_preset_id: string | null;
  ai_questions: AIQuestion[];
  ai_uploaded_files: UploadedFile[];
  ai_extra_prompt: string;
  sent_to_client: boolean;
  client_answered: boolean;
}

export interface AIBizplanExtras {
  ai_preset_id: string | null;
  ai_uploaded_files: UploadedFile[];
  ai_opt_doc_files: Record<string, UploadedFile>;
  ai_extra_prompt: string;
  ai_result: string;
}

export type StepExtras =
  | CollectExtras
  | AIQuestionnaireExtras
  | AIBizplanExtras
  | Record<string, never>;

// ═══ 업무 ═══

export type StepType =
  | 'work' | 'collect' | 'ai_questionnaire' | 'ai_bizplan'
  | 'confirm' | 'contract' | 'meeting' | 'report' | 'invoice';

export type StepStatus = '대기' | '진행' | '회신대기' | '완료';   // 회신대기 — 업체 · FP 응답 기다림 (2026-10-12)
export type TaskStatus = 'FP요청대기' | '진행' | '보류' | '중단' | '완료';   // 보류 = 잠시 멈춤 · 중단 = 업체가 안 하기로 함 (2026-10-12)

export interface TaskStep {
  id: string;
  task_id: string;
  name: string;
  type: StepType;
  status: StepStatus;
  due: string | null;
  sort_order: number;
  note: string;
  extras: StepExtras;
  created_at: string;
  updated_at: string;
}

export interface Task {
  id: string;
  account_id: string;
  company_id: string;
  template_id: string | null;
  type: string;
  icon: string;
  category_path: string[];
  status: TaskStatus;
  progress: number;
  deadline: string | null;
  assigned_to: string;
  note: string;
  waiting_fp: boolean;
  consult_id: string | null;
  /** 벤처인증 모듈 조합 (157 · 2026-10-18 업무관리에서 등록한 벤처(투자) 업무도 잇는다) */
  venture_project_id?: string | null;
  /** 중단 이유 한 낱말 · 날짜 (2026-10-12 · FP 포털에만 이유가 보임) */
  stop_reason?: string | null;
  stopped_at?: string | null;
  steps: TaskStep[];
  created_at: string;
  updated_at: string;
  sort_order?: number;
}

// ═══ 상담 ═══

export type ConsultStatus = 'scheduled' | 'completed' | 'sent';

export interface CandidateSlot {
  date: string;
  time: string;
  location: string;
}

export interface CustomStep {
  name: string;
  type: string;
  _aiPresetId?: string;
  _collectIds?: string[];
}

export interface EditStep {
  name: string;
  type: string;
  due: string;
  _aiPresetId?: string;
  _taskAiPrompt?: string;
}

export interface TaskDecision {
  id: string;
  mode: 'template' | 'custom';
  templateId?: string;
  customName?: string;
  customIcon?: string;
  customSteps?: CustomStep[];
  editSteps?: EditStep[];
  taskStatus?: string;
  deadline?: string;
}

export interface Consultation {
  id: string;
  /** 함께 상담한 회사(관계사 · 178 · 2026-10-18) — 주 회사(company_id) 말고 */
  extra_company_ids?: string[] | string | null;
  /** 다음 진행 — 업무 진행 / 종결 (160 · 2026-10-12) */
  next_action?: '업무' | '종결' | '' | null;
  close_reason?: string | null;
  account_id: string;
  company_id: string;
  fp_id: string | null;
  date: string;
  time: string;
  location: string;
  topic: string;
  status: ConsultStatus;
  content: string;
  files: UploadedFile[];
  task_decisions: TaskDecision[];
  notes: string;
  ref_files: UploadedFile[];
  sent_to_fp: boolean;
  sent_at: string | null;
  confirm_status: 'confirmed' | 'tentative';
  candidate_slots: CandidateSlot[];
  fee_type: 'paid' | 'free';
  fee_method: 'individual' | 'fa_settle' | null;
  fee_amount: number;
  fee_memo: string;
  company_name?: string;
  fp_name?: string;
  fp_phone?: string;
  fp_company?: string;
  /** 첫 분(옛 화면 호환) */
  wm_id?: string | null;
  wm_name?: string;
  /** 함께한 WM 여러 명(공동관리 · 2026-10-26) — 첫 분 = wm_id · 이름은 같은 순서 */
  wm_ids?: string[];
  wm_names?: string[];
  /** 계약 가능성(2026-10-29 · 237) — 유망 · 보통 · 어려움 · 안 고르면 null */
  deal_outlook?: '유망' | '보통' | '어려움' | null;
  deal_reason?: string;
  created_at: string;
  updated_at: string;
}

// ═══ 코멘트 ═══

export interface Comment {
  id: string;
  account_id: string;
  company_id: string;
  author: string;
  content: string;
  files: UploadedFile[];
  fp_visible: boolean;
  /** 고객 페이지 「임지도사의 안내」에 보임 (2026-10-12) */
  customer_visible?: boolean;
  sms_sent: boolean;
  created_at: string;
}

// ═══ 정부지원사업 ═══

export interface GovProgram {
  id: string;
  account_id: string;
  /** FP 전송용 사업명 — 「[대구 동구] 이노비즈·메인비즈·벤처 인증취득 수수료 지원」 (지난 사업은 report_name 과 같다) */
  name: string;
  /** 리포트 사업명 (2026-10-01) — fphub 정부지원 리포트에 보이는 공식 사업명만 「초기창업패키지」. null 이면 피드가 name 에서 만든다 */
  report_name?: string | null;
  /** 리포트 짧은 이름 · 대표 주관기관 (2026-10-07 · 149) — brief_by 'ai' | 'manual' */
  brief_name?: string | null; lead_agency?: string | null; brief_by?: string | null;
  /** 정식 사업명(고유명) — 리포트 로드맵 1줄 (2026-10-17) */
  canon_name?: string | null;
  deadline: string | null;
  agency: string;
  amount: string;
  category: string;
  region: string;
  region_detail?: string;
  tags: string[];
  target_biz_types: string[];
  target_company_scale: string;
  /** 대표 만나이 상한 — null/미지정이면 무관 */
  target_ceo_age_max?: number | null;
  /** 대표 만나이 하한 — 시니어 창업 등. null/미지정이면 무관 */
  target_ceo_age_min?: number | null;
  target_startup: string;
  /** 신청 시작일 (YYYY-MM-DD) — 「미리 준비」 안내용. null 이면 미지정 */
  apply_start?: string | null;
  /** 매년 반복되는 공고인지 — 「전년도 공고 복제」 대상 표시에 쓰인다 */
  recur_yearly?: boolean;
  /** 모집 달(2026-10-14) — NULL = 자동(수집 공고 회차에서) · '[]' = 여러 번 아님 · '[2,4,10]' = 직접 정함. 저장은 배열로 보낸다 */
  apply_months?: string | number[] | null;
  /** 지원금액(만원, 숫자) — 기존 자유 문자열 amount 는 표기용으로 유지 */
  amount_max_manwon?: number | null;
  /** 연매출 조건 연산자 '<'|'<='|'>='|'>' (빈 문자열 = 무관) */
  target_sales_op?: string;
  /** 연매출 조건 값(억원) */
  target_sales_eok?: number | null;
  /** 상시근로자수 조건 연산자 */
  target_emp_op?: string;
  /** 상시근로자수 조건 값(명) */
  target_emp_n?: number | null;
  /** 업력 최소(년) */
  target_yrs_min?: number | null;
  /** 업력 최대(년) */
  target_yrs_max?: number | null;
  /** 특허 보유 권장(가점만, 필수 아님) */
  want_patent?: boolean;
  /** 상표 보유 권장(가점만, 필수 아님) */
  want_tm?: boolean;
  exclude_micro?: boolean;
  /** 세부 유형 (2026-10-05) — 대상·금액이 다른 유형이 있을 때만. 서버가 정규화해 돌려준다(비었으면 []) */
  tracks?: GovTrack[];
  /** 공고 공통 확장 조건 (2026-10-05) — 업종·지역 제외, 매출 감소율, 수출실적, 개업일, 「또는」 묶음, FP 확인 항목, 내부 메모 */
  conditions?: GovConditions;
  matching_tags: { required: { id: string; label: string }[]; recommended: { id: string; label: string }[] } | null;
  link: string;
  short_url: string;
  first_come: boolean;
  is_active: boolean;
  memo: string;
  created_at: string;
  updated_at: string;
  // ═══ 같은 공고 묶음 · 상품 아닌 공고 (2026-10-05 · 마이그레이션 148 · backend services/govDup.js) — 서버가 저장할 때 계산 · 148 전 서버 응답엔 없다 ═══
  /** 같은 공고 묶음 열쇠(= 대표 공고 id). null = 묶음 없음 */
  dup_group?: string | null;
  /** 묶음 대표면 true (묶음 밖도 true). 목록 · 단톡방 · 리포트는 대표 한 줄만 */
  dup_primary?: boolean;
  /** 상품 아닌 공고 — 'agency_recruit'(기관 모집) · 'umbrella'(통합 공고 — 가림) · 'umbrella_unsplit'(통합 공고 — 세부 상품 없어 보임) */
  hidden_kind?: GovHiddenKind | null;
  /** 지도사 「같은 공고 아님」 — 다시 묶지 않는다 */
  dup_off?: boolean;
  /** 지도사 「보이기」 — hidden_kind 가 있어도 상품으로 */
  hide_off?: boolean;
  /** 「같은 공고 확인」 — 같다고 하긴 모자라지만 가까운 공고(dup_review_of)가 있어 지도사 확인이 필요 */
  dup_review?: boolean;
  dup_review_of?: string | null;
  /** 「묶기」 — 지도사가 손으로 붙인 대표 id */
  dup_pin?: string | null;
  // ═══ 같은 사업 묶음 (2026-10-07 · 마이그레이션 151 · backend services/govFeed/progGroup.js) — 같은 공고(dup_group)와 다르다: 운영기관 · 차수 · 유형만 다른 «같은 사업» ═══
  /** 같은 사업 묶음 id — null = 묶음 없음(혼자는 묶음이 아니다) */
  prog_group?: string | null;
  /** 묶음 안에서 지원 내용 · 대상이 다른 유형 이름(육아휴직 · 대체인력 · 일반형) — 없으면 '' */
  prog_part?: string | null;
  /** 'ai' | 'manual' — 지도사가 직접 정하면 AI 가 다시 바꾸지 않는다 */
  prog_group_by?: string | null;
  prog_group_note?: string | null;
}
export type GovHiddenKind = 'agency_recruit' | 'umbrella' | 'umbrella_unsplit';

// ═══ 「공고 고치기」 (2026-10-07g · backend services/govFeed/reportFix.js · routes/tm-gov.js /gov-programs/:id/fix) — 리포트(fphub 관리자)와 같은 액션을 로움 화면에서도 ═══
/** 묶음 식구 한 줄 — part = 유형 · by = 'ai' | 'manual'(직접 정함) */
export interface GovFixMember { id: string; name: string; brief_name: string; agency: string; part: string; by: string | null }
export interface GovFixGroup { group: string | null; by: string | null; members: GovFixMember[] }
export interface GovFixTrack { id?: string; name: string; amount: string; category: string; form: string; cat_by: string }
/** 패널이 보여 줄 지금 값 한 벌 (reportFix.editStateOf) */
export interface GovFixState {
  id: string; name: string; report_name: string; brief_name: string; brief_by: string | null;
  category: string; cat_by: string; agency: string; link: string; region: string; region_detail: string;
  biz: string[]; biz_by: string | null; subs: string[]; sec: string[]; off: { k: string; label: string }[];
  tech: boolean; bizx: string[]; form: '' | 'consult'; form_by: string | null; reviewed: boolean; reviewed_by: string | null;
  tracks: GovFixTrack[]; group: GovFixGroup; canon_name: string;
}
/** POST 바디 — programId 는 주소(:id)가 정한다 · group_merge 의 programIds 는 «다른» 공고 id(서버가 자기 id 를 앞에 넣는다) */
export type GovFixAction =
  | { action: 'group_split'; all?: boolean; reason?: string; note?: string }
  | { action: 'group_merge'; programIds: string[]; reason?: string; note?: string }
  | { action: 'brief'; brief_name: string; reason?: string; note?: string }
  | { action: 'category'; category: string; track?: string; form?: '' | 'consult'; reason?: string; note?: string }
  | { action: 'targets'; biz: string[]; subs: string[]; sec: string[]; bizx: string[]; tech: boolean; form: '' | 'consult'; region: string | null; region_detail: string | null; reason?: string; note?: string };
export interface GovFixResult {
  saved: boolean; action: string; changed: string[]; learned: boolean;
  /** 이유를 안 적었을 때 — 뒤에 action 'reason' 으로 붙일 학습 초안 */
  draft?: { field: string; before: string; after: string; evidence: string; label: string };
  state: GovFixState | null;
}

// ═══ 지원사업 세부 유형 · 확장 조건 (2026-10-05 명세 — backend services/govTracks.js 와 같은 모양) ═══

/** 비교 연산자 (상시근로자·연매출) */
export type GovRuleOp = '<' | '<=' | '>=' | '>';
/** 기업규모 코드 — 소상공인 ⊂ 소기업 ⊂ 중소기업 ⊂ 중소·중견 */
export type GovScaleCode = 'micro' | 'small' | 'ms' | 'msmid';
/** 트랙 금액 단위 */
export type GovTrackUnit = '' | '사업장' | '1인' | '현장' | '월';

/** 판정 규칙 — 트랙 rules · conditions · 「또는」 묶음 원소가 같은 키를 쓴다 (모든 키 선택) */
export interface GovRules {
  rg?: string[]; rgd?: string[]; biz?: string[]; types?: string[]; arts?: string[]; cls?: string[];
  /** 하위 업종 키 · 특수 자격 (2026-10-05g) */
  subs?: string[]; spec?: string[];
  scale?: GovScaleCode;
  startup?: true | 'exclude';
  age?: { min?: number; max?: number };
  yrs?: { min?: number; max?: number };
  emp?: { op: GovRuleOp; n: number };
  sales?: { op: GovRuleOp; eok: number };
  nomicro?: true; pat?: true; tm?: true;
  /** 기술 기반 창업 (2026-10-05l) — 업종을 정하지 않았으면 제조 · 연구개발 · SW 한정 */
  tech?: true;
  /** 업종 제외 (로움 업종 라벨) */
  bizx?: string[];
  /** 지역 제외 (수도권 = 서울·경기·인천) */
  rgx?: string[];
  /** 전년 대비 매출 감소율(%) 이상 */
  drop?: { pct: number };
  /** 최근 1년 수출실적(USD) */
  exp?: { op: '<' | '>='; usd: number };
  /** 개업일 — before 이전 / after 이후 (YYYY-MM-DD) */
  opened?: { before?: string | null; after?: string | null };
  /** 「또는」 묶음 — 하나라도 맞으면 (안에 또 any 는 없다) */
  any?: GovRules[];
  /** FP 확인 항목 — 판정에 영향 없음, 체크 목록 */
  chk?: string[];
}

/** 내부 보관 메모 — 판정·리포트·FP 화면에 쓰지 않는다 (자부담·지원비율 등은 화면에 입력칸 없이 보존만) */
export type GovMeta = Record<string, string | number | boolean>;

/** 공고 공통 확장 조건 */
export interface GovMicroOk { all?: boolean; biz?: string[]; types?: string[]; check?: { t: string; biz?: string[] } }
export interface GovConditions {
  micro_ok?: GovMicroOk;   // 소상공인 제외의 예외 — 공고문 단서(2026-10-26)
  bizx?: string[];
  rgx?: string[];
  drop?: { pct: number };
  exp?: { op: '<' | '>='; usd: number };
  opened?: { before?: string | null; after?: string | null };
  any?: GovRules[];
  chk?: string[];
  nomicro?: true;
  /** 기술 기반 창업 (2026-10-05l 지도사) — 업종을 직접 정하지 않았으면 제조업 · 연구개발업 · IT/SW개발업 한정. 검토함(govTargets)이 본문 「기술 기반」으로 자동으로 켜고(sec_auto k:'tech') 사람이 끌 수 있다 */
  tech?: true;
  /** 지원형태 (2026-10-07 지도사 「우리가 컨설팅 회사인데」) — 'consult' = 컨설팅 · 진단 · 교육만(자금 없음) → 매칭 · 리포트에서 뺀다 · form_by = ai | manual | feedback(리포트에서 뺀 이유) */
  form?: 'consult';
  form_by?: 'ai' | 'manual' | 'feedback';
  /** 업종 칸 출처 (2026-10-07f) — 'manual' = 지도사가 직접 고름(AI 다시 읽기가 업종을 바꾸지 않는다) · 'ai' = AI 가 읽은 업종 · biz_prev = AI 가 바꾸기 전 업종 */
  biz_by?: 'ai' | 'manual';
  biz_prev?: string[];
  meta?: GovMeta;
  /** 하위 업종 키 (2026-10-05g) — '제조업›식품' · 업종 무관 공통 분야 '*›반려동물' */
  subs?: string[];
  /** 전용 대상 (2026-10-05f · g) — 켠 라벨(특수 자격 · 자동으로 찾은 기업 유형) · «아님»으로 뺀 키 · 라벨 · 자동 체크 기록 */
  sec?: string[];
  sec_off?: string[];
  sec_auto?: GovSecAuto;
  /** 지원 종료 달 'YYYY-MM' (2026-10-18 지도사 「2027년 0원 사업 — 내년 1월부터는 공란」) — 리포트 달력은 그 달까지 · 지나면 싣지 않음 */
  end_ym?: string;
}
/** 자동으로 찾은 하위 업종 · 전용 대상 1개 — from: 사업명 · 본문 · AI 판정 · 옛 분류(2026-10-05g 옮기기) · hint = 낱말 판정 «참고»(AI 가 확인하지 않아 켜지 않은 값 · 2026-10-05o)
 *  ev: 근거 문장(AI 판정은 공고문 인용) · m: 잡힌 낱말 · conf: AI 확신 · src: 참고 값의 원래 출처 */
export type GovAiConf = 'high' | 'mid' | 'low';
export interface GovSecAutoItem { k: string; from: 'title' | 'body' | 'ai' | 'old' | 'hint'; ev: string; m?: string; conf?: GovAiConf; src?: 'title' | 'body' | 'ai' | 'old' }
/** AI 판정 기록(services/govTargetsAi.js) — 언제 · 어떤 모델 · 확신 · 메모 · 직접 값과 다른 점(-뺌 +켬) · AI 가 읽은 업종 */
export interface GovSecAutoAi { at?: string; model?: string; conf?: GovAiConf; note?: string; biz?: string[]; bizx?: string[]; diff?: string[] }
export interface GovSecAuto { items: GovSecAutoItem[]; at?: string; reviewed: boolean; by?: string; rat?: string; ai?: GovSecAutoAi }
/** 전용 대상 검토 화면 1줄 (GET /gov-targets/review) */
export interface GovTargetRow {
  id: string; name: string; report_name: string; agency: string; deadline: string | null; first_come: boolean; category: string;
  biz?: string[]; subs?: string[];
  sec: string[]; sec_off: string[]; reviewed: boolean; at: string | null; by: string | null;
  /** 필수 자격 (2026-10-08) — t 공고 글 · k have(이미 갖춰야) | gain(신청 전에 갖추면 됨) · f 업체 사실 이름(하나라도) · ev 원문 */
  req?: { t: string; k: 'have' | 'gain'; f: string[]; ev: string }[];
  /** label = 화면 글(「제조업 › 소방·안전」) · kind = 하위 업종 · 기업 유형 · 특수 자격 · inAny = 「또는」 묶음 안(옛 분류 옮김) · on = 참고 값이 켜져 있는가(지도사가 켠 경우) */
  items: (GovSecAutoItem & { off: boolean; strict: boolean; label?: string; kind?: 'sub' | 'type' | 'special'; inAny?: boolean; on?: boolean; outside?: boolean; outsideLabel?: string })[];
  /** AI 판정 기록 — 확신 · 메모 · 직접 값과 다른 점 (2026-10-05o) */
  ai?: { at: string | null; model: string | null; conf: GovAiConf | null; note: string; biz: string[]; diff: string[]; removed?: string[]; titleHint?: string[]; tracksNew?: string[] } | null;   // removed · titleHint — 2026-10-07f · tracksNew(AI 가 새로 나눈 세부 유형) — 2026-10-07g
}
export interface GovTargetSnap { subs?: string[]; sec: string[]; sec_off: string[]; reviewed: boolean }

/** 세부 유형 (트랙) — 트랙 규칙에 적은 것만 공통 규칙을 덮는다(chk·any 는 합친다) */
export interface GovTrack {
  id: string;
  name: string;
  /** 표기용 금액 문구 「최대 3,000만원」 */
  amount: string;
  amount_max_manwon: number | null;
  unit: GovTrackUnit;
  apply_start?: string | null;
  deadline?: string | null;
  first_come?: boolean;
  rules: GovRules;
  meta?: GovMeta;
  /** 리포트 칸(세부 분류 이름 — GOV_TAXONOMY 무상 항목) · '' = 사업 분류를 따른다 (2026-10-07f) */
  category?: string;
  /** 'consult' = 컨설팅 · 교육만 받는 유형 — 리포트에 싣지 않는다 (2026-10-07f) */
  form?: '' | 'consult';
  /** 칸 · 지원형태를 누가 정했나 — 'manual' 이면 AI 다시 읽기가 손대지 않는다 (2026-10-07f) */
  cat_by?: '' | 'ai' | 'manual';
}

/** 저장 전 미리보기 (POST /gov-programs/preview) */
export interface GovPreviewSample {
  key: string;
  label: string;
  verdict: 'pass' | 'near' | 'fail';
  track: { name: string; amount: string } | null;
  why: string;
}
export interface GovPreviewResult {
  samples: GovPreviewSample[];
  mine: { pass: number; near: number; total: number };
  /** 사업명으로 알아본 전용 대상 (2026-10-05f) · 엔진이 적용하는 전용 대상 전체 */
  targets?: { k: string; from: string; m: string }[];
  subs?: string[];
  sec?: string[];
}

// ═══ AI 프리셋 ═══

export interface AIPreset {
  id: string;
  account_id: string;
  name: string;
  type: 'ai_questionnaire' | 'ai_bizplan';
  docs: UploadedFile[];
  opt_docs: UploadedFile[];
  prompt: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// ═══ 카테고리 / 템플릿 ═══

export interface TaskCategory {
  id: string;
  account_id: string;
  name: string;
  parent_id: string | null;
  depth: number;
  sort_order: number;
}

export interface TemplateStep {
  name: string;
  type: string;
  due_days: number;
  /** 순서(서버가 그대로 적어 둠) */
  sort_order?: number;
  note: string;
  collect_ids: string[];
  use_ai: boolean;
  settings?: Record<string, unknown>;
  /** 누가 할 일 · 안내 글 · 받을 서류(2026-10-28 · tm/stepOwner) — 대표님 · 팀장님 몫이면 이 단계가 되었을 때 「답 기다림」으로 열린다 */
  owner?: 'jido' | 'ceo' | 'fp' | 'both';
  ask?: string;
  ask_items?: string[];
  /** 결과 단계 말 — 고르면 끝낼 때 「✓ 승인」 「미승인」(2026-10-28 · components/JudgeSelect) */
  judge?: { yes: string; no: string } | null;
}

export interface TaskTemplate {
  id: string;
  account_id: string;
  name: string;
  icon: string;
  category_id: string | null;
  cat_path: string[];
  steps: TemplateStep[];
  /** 코드가 찾는 템플릿(벤처 GP · LP 등 · 2026-10-28) — 지울 수 없고 단계 뼈대는 그 화면 기준 */
  system_key?: string | null;
  total_days: number;
  kb: Record<string, string>;
  bp_sections: BPSection[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// ═══ API 응답 ═══

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

export interface PaginatedCompanies {
  companies: Company[];
  total: number;
  page: number;
  limit: number;
}

export interface StepUpdateResult {
  step: TaskStep;
  progress: number;
  taskStatus: string;
}

export interface TMSettings {
  logoText: string;
  senderName: string;
  companyName: string;
  phone: string;
}

// ═══ Step Config 타입 ═══

export interface CollectItemGuide {
  type: 'link' | 'file';
  label: string;
  url: string;
  fileName: string;
  s3Key: string;
}

export interface CollectItem {
  id: string;
  label: string;
  type: 'file' | 'text' | 'text_long' | 'radio' | 'checkbox';
  required: boolean;
  sensitive: boolean;
  description: string;
  section?: string;
  guides: CollectItemGuide[];
  options?: string[];
}

export interface CollectStepConfig {
  items: CollectItem[];
  refFiles: { name: string; s3_key: string }[];
  refLinks: string[];
  baseTemplateId: string;
  brand?: 'roum' | 'atv' | 'law';   // 고객 제출 화면 우측 상단 로고 — 기본 roum (2026-09-11) · law 로움법무사사무소(2026-10-31)
}

export interface AIStepConfig {
  presetId: string;
  files: { label: string; name: string; s3_key: string }[];
  extraPrompt: string;
  phase: 'settings' | 'generating' | 'reviewing' | 'done';
  result: string;
  includeCollectAnswers?: boolean;
}

export interface AIBizplanConfig {
  presetId: string;
  files: { label: string; name: string; s3_key: string }[];
  extraPrompt: string;
  phase: 'settings' | 'generating' | 'reviewing';
  includeCollectFiles?: boolean;
}

export interface TaskDecisionItem {
  id: string;
  /** 어느 회사 업무인지 — 관계사와 함께 상담했을 때(2026-10-18) · 비면 상담의 주 회사 */
  companyId?: string;
  /** 담당(2026-10-18) — 오재인 · 임우승 · 비면 오재인 */
  who?: string;
  mode: 'template' | 'custom';
  templateId: string;
  customName: string;
  customIcon: string;
  taskStatus: '진행' | 'FP요청대기';
  deadline: string;
  steps: { name: string; type: string; due: string; aiPresetId?: string; settings?: Record<string, unknown> }[];
  saveAsTemplate: boolean;
  category_path?: string[];
  /** 벤처인증(투자) — GP = 새 조합(대표 = 조합장) · LP = 다른 조합에 참여(projectId) · 조합 미정이면 LP 후보 (2026-10-18 · services/ventureFromTask) */
  venture?: { role: 'gp' | 'lp'; org?: string; projectId?: string; projectOrg?: string; office?: 'roum' | 'atv'; investPlan?: string };   // office = 진행 주체(새 조합 · LP 후보) — 고른 조합에 참여하면 그 조합의 주체
}

export interface TodoAttachment {
  id: string;
  fileName: string;
  s3Key: string;
  size: number;
  mimeType: string;
  uploadedBy: string;
  uploadedAt: string;
}

export type TodoStatus = 'pending' | 'partial' | 'review' | 'notify' | 'done';

export interface Todo {
  id: string;
  title: string;
  description: string | null;
  created_by: string;
  assigned_to: string;
  assignees: string[];
  /** pending 요청 · partial 진행 중 · review 확인 기다림 · notify 안내 남음 · done 완료 (2026-10-22 결과 확인 흐름) */
  status: TodoStatus;
  progress: number;
  progress_note: string | null;
  /** 결과 확인을 받는 일(맡긴 사람이 결과를 보고 확인 → 안내) */
  needs_check?: boolean;
  notify_to?: string | null;
  notify_phone?: string | null;
  result_note?: string | null;
  result_by?: string | null;
  result_at?: string | null;
  check_note?: string | null;
  checked_at?: string | null;
  notify_by?: 'staff' | 'me' | null;
  due_date: string | null;
  snoozed_until: string | null;
  completed_at: string | null;
  attachments: TodoAttachment[];
  is_template: boolean;
  recur_type: 'daily' | 'weekly' | null;
  recur_days: string | null;
  recur_last_at: string | null;
  parent_id: string | null;
  created_at: string;
  updated_at: string;
  comment_count?: number;
  /** 내가 아직 안 본 남의 댓글 수(2026-10-27) — 카드가 깜빡인다 */
  unread_comments?: number;
}

export interface TodoComment {
  id: string;
  todo_id: string;
  author_id: string;
  author_name?: string;
  content: string;
  attachments: TodoAttachment[];
  created_at: string;
  updated_at: string;
}

// ═══ 공고 검토함 (기업마당 → AI 조건 분석 → 검토·승인) — routes/tm-gov-feed.js ═══

export type GovFeedReview = 'new' | 'approved' | 'excluded';
export type GovFeedScreen = 'pending' | 'fit' | 'maybe' | 'unfit';
export type GovFeedAiStatus = 'none' | 'queued' | 'batched' | 'running' | 'done' | 'error' | 'skipped' | 'past'; // past = 마감된 일회성 — 자동 분석 안 함 (2026-10-01)

export interface GovFeedJob { kind: 'sync' | 'screen' | 'analyze'; done: number; total: number; running: boolean }

export interface GovFeedStats {
  keyConfigured: boolean;
  aiConfigured: boolean;
  lastSync: { at: string; ok: boolean; stats: Record<string, unknown> | null; error: string | null } | null;
  job: (GovFeedJob & { source?: string }) | null;
  /** new·approved·excluded 는 전체, 나머지는 검토 대기(review=new) 안에서 센 값 */
  counts: {
    new: number; approved: number; excluded: number;
    fit: number; maybe: number; unfit: number; pending: number;
    aiDone: number; aiQueued: number; aiError: number; dup: number;
    /** Message Batch 결과 대기 (aiQueued 에 포함) */
    aiBatched?: number;
    buckets?: Partial<Record<GovFeedBucket, number>>;
  };
  sources?: GovFeedSourceStat[];
  /** AI 사용량 (표가 없으면 null) */
  usage?: { today: GovFeedUsage; total: GovFeedUsage } | null;
  lastError: string | null;
  dailyMax: number;
  /** 하루 상한 없음 — 총예산이 막는다 (2026-10-03) */
  dailyUnlimited?: boolean;
  analyzedToday: number;
  /** Message Batches (메모 5) */
  batches?: { inFlight: number; requestsInFlight: number; lastEndedAt: string | null } | null;
  batchMode?: boolean;
  /** 총예산 (메모 6 — 절대 초과 금지) */
  budget?: GovFeedBudget | null;
  pipelineState?: 'running' | 'budget_stopped' | 'error' | 'idle';
  /** 분석 범위 (2026-10-03) — local=false 면 서울·경기·인천·전국만 분석 · 대기 건수(범위 안 / 지방) */
  scope?: GovFeedScope | null;
}

export interface GovFeedRow {
  id: string;
  title: string;
  baseName: string;
  ministry: string;
  agency: string;
  field: string;
  occurrences: number;
  /** 회차 연도 쉼표 문자열 (예 "2025,2026") */
  years: string;
  latestEnd: string | null;
  latestRegistered: string | null;
  screen: GovFeedScreen;
  screenReason: string | null;
  screenCat: string | null;
  aiStatus: GovFeedAiStatus;
  review: GovFeedReview;
  dupProgramId: string | null;
  govProgramId: string | null;
  /** AI 적합도 0~100 (분석 전 null) */
  fit: number | null;
  regionHint: string;
  /** AI 사업명 — 목록 API 가 주면 쓴다(없으면 화면이 baseName 으로 대신) */
  aiName?: string | null;
  /** 대량 검토 탭 (2026-09-30 22:07 추가) — 서버가 안 주면 화면이 screen·aiStatus·fit 으로 대신 판단 */
  bucket?: GovFeedBucket;
  /** draft||prefill 표시용 요약 */
  program?: GovFeedRowProgram | null;
  unsureCount?: number;
  /** 사용자가 표·패널에서 고친 값(draft)이 있다 */
  edited?: boolean;
  /** 관심 별표 (2026-10-03) */
  starred?: boolean;
  /** 이 공고(최신 회차)를 불러온 날 YYYY-MM-DD (2026-10-04) */
  collectedAt?: string | null;
  /** 같은 공고(다른 채널)로 묶여 이 행 뒤에 숨은 공고 수 (2026-10-03) */
  dupCount?: number;
  /** 이 묶음 공고가 들어온 출처 ('bizinfo'|'naver'|'sbiz24', 기업마당 먼저) */
  sources?: string[];
}

export interface GovFeedBudget {
  krw: number; rate: number; usdLimit: number; spentUsd: number; spentKrw: number; reservedUsd: number; reservedKrw: number; remainingKrw: number;
  perRequestEstKrw: number; perRequestBasis: 'initial' | 'measured'; measuredN: number;
  remainingGroups: number; projectedTotalKrw: number; stopped: boolean; stoppedAt: string | null; stopReason: string | null;
  resumed?: boolean;
}
export interface GovFeedUsageRow { calls: number; inTok: number; outTok: number; estUsd: number }
export interface GovFeedUsage extends GovFeedUsageRow { byKind: Record<string, GovFeedUsageRow> }

export type GovFeedBucket = 'ready' | 'check' | 'maybe' | 'unfit' | 'pending' | 'past';

/** 목록 행의 등록값 요약 (draft||prefill) — category 는 배열·쉼표 문자열 둘 다 받는다 */
export interface GovFeedRowProgram {
  name?: string | null;
  /** 리포트 사업명 (2026-10-01) */
  reportName?: string | null;
  regions?: string[] | string | null; regionDetails?: string[] | string | null;
  category?: string[] | string | null;
  amount?: string | null; amountMaxManwon?: number | null;
  applyStart?: string | null; deadline?: string | null; firstCome?: boolean; recurYearly?: boolean;
  companyScale?: string | null; startup?: string | null;
  yrsMin?: number | null; yrsMax?: number | null; ceoAgeMax?: number | null;
}

/** 소스별 마지막 수집 (stats.sources) */
export interface GovFeedSourceStat {
  key: string; label: string; lastAt: string | null; ok: boolean | null; count: number | null; error: string | null;
  running?: boolean; keyConfigured?: boolean; linked?: number; lastStats?: Record<string, unknown> | null;
}

// ── 원문 보기 · 교정 학습 (추가 확정 3) ──
export interface GovFeedDoc { key: string; kind: 'pdf' | 'image' | 'text'; fileName: string; fileUrl: string | null; viewUrl: string | null; text?: string | null; /** HWP·HWPX·Word 를 서버가 PDF 로 바꿔 보여 줌 (2026-10-01) */ converted?: boolean; /** 원본 서버에서 받지 못함 — 이유 (2026-10-03) */ error?: string }
export interface GovFeedDocItem { itemId: string; source: string; title: string; registeredAt: string | null; docs: GovFeedDoc[] }
/** 「AI 다시 읽기」 응답 (POST /gov-targets/:id/ai) */
export interface GovTargetAiResult {
  row: GovTargetRow; conditions: GovConditions; changed: boolean; reviewed: boolean; added: string[]; removed: string[]; hinted: string[]; offAdded: string[]; manualDiff: string[]; bizxAdded: string[]; bizxRemoved: string[];
  conf: GovAiConf; note: string; dropped: string[]; model: string | null;
}
export interface GovFeedRule {
  id: number; text: string; field: string | null; source: 'learned' | 'manual'; enabled: boolean; edited: boolean; hits: number;
  /** 판정 원칙(seed) 규칙의 고정 이름표 'targets.NN' — 지도사가 직접 쓴 규칙은 null (2026-10-05o) */
  key?: string | null;
  createdAt: string; updatedAt: string;
}
export interface GovFeedRulesInfo {
  rules: GovFeedRule[];
  accuracy: { overall: number | null; byField: Record<string, { n: number; agree: number }>; recentN: number };
  feedbackCount: number; lastLearnedAt: string | null; learning: boolean; learnEvery: number;
  /** 마지막으로 성공한 학습의 결과 (2026-10-03) */
  lastLearnStats?: { feedbackUsed: number; learned: number; kept: number; trigger: string | null } | null;
  /** 마지막 학습 시도가 실패했으면 (성공한 학습보다 나중일 때만) */
  lastLearnError?: { at: string; error: string } | null;
}

export interface GovFeedBulkApproveResult {
  results: { id: string; ok: boolean; programId?: string; mode?: 'new' | 'update'; warning?: 'duplicate' | 'dupMissing'; error?: string }[];
  okCount: number; failCount: number;
}

/** 같은 조건의 건수 (counts=1 — 2026-10-03) · 지역 키 '전국' = 지역 지정 없는 공고 · cities 는 광역 하나를 골랐을 때만 */
export interface GovFeedFacets {
  buckets: Record<GovFeedBucket, number> | null;
  regions: Record<string, number>; cities: Record<string, number>; cats: Record<string, number>; catGroups: Record<string, number>;
  /** 같은 탭·조건의 관심(별표) 건수 */
  starred?: number;
}
export interface GovFeedGroupsPage { rows: GovFeedRow[]; total: number; page: number; size: number; facets?: GovFeedFacets }
/** 지난 공고 분석 범위 — pick: ★ 고른 것만 · capital: 수도권·전국 · local: 지방까지 (새 공고는 늘 모두) */
export type GovFeedScopeMode = 'pick' | 'capital' | 'local' | 'manual';   // manual = 수기 판독(★ 자동 분석 끔 · 2026-10-17)
export interface GovFeedScope { mode?: GovFeedScopeMode; local: boolean; waitingCapital: number; waitingLocal: number; waitingPicked?: number; waitingStarred?: number }
/** 검토함 머리글 정렬 (서버 sort 값) */
export type GovFeedSortKey = 'fit' | 'name' | 'region' | 'cat' | 'amount' | 'deadline' | 'collected';
export interface GovFeedSort { k: GovFeedSortKey; dir: 'asc' | 'desc' }

/** AI_RESULT — GovProgramForm 칸과 1:1 (계약서) */
export interface GovFeedAiResult {
  fit: number | null; fitReason: string;
  name: string; agency: string; summary: string; targetText: string;
  /** 리포트 사업명 — 공식 사업명만 (2026-10-01, 이전 분석 결과엔 없음) */
  report_name?: string;
  /** 분석 때 지난 공고(마감된 일회성)였는지 — 이때 name = report_name */
  pastNotice?: boolean;
  category: string[];
  amount: string; amountMaxManwon: number | null;
  applyStart: string | null; deadline: string | null; firstCome: boolean; recurYearly: boolean;
  regions: string[]; regionDetails: string[]; bizTypes: string[];
  companyScale: string; startup: string;
  ceoAgeMin: number | null; ceoAgeMax: number | null;
  salesOp: string; salesEok: number | null; empOp: string; empN: number | null;
  yrsMin: number | null; yrsMax: number | null; wantPatent: boolean; wantTm: boolean;
  /** 세부 유형 · 확장 조건 (2026-10-05, 이전 분석 결과엔 없음) — 저장 모양 그대로 */
  tracks?: GovTrack[];
  conditions?: GovConditions;
  /** 전용 대상 — 특수 자격 (2026-10-05f · 이전 분석 결과엔 옛 업종 전용 라벨) */
  targets?: string[];
  /** 하위 업종 키 (2026-10-05g · 이전 분석 결과엔 없음) */
  subs?: string[];
  evidence: Record<string, string>;
  unsure: string[];
  source?: { detail?: boolean; file?: 'hwp' | 'hwpx' | 'pdf' | 'image' | 'docx' | null; chars?: number; notes?: string[]; pdfMode?: 'text' | 'document'; batch?: boolean };
}

export interface GovFeedItem {
  pblancId: string; title: string;
  applyStart: string | null; applyEnd: string | null; applyText: string | null;
  registeredAt: string | null; url: string;
  source: 'excel' | 'api'; fileName: string | null;
}

/** 같은 공고로 묶인 다른 채널 공고 — reason: file(공고문 같음) · name(공고문 이름 같음) · title(사업명·마감·제목) */
export interface GovFeedSameNotice { id: string; title: string; agency: string | null; reason: string | null; url: string | null; source: string | null; fileName: string | null; registeredAt: string | null }
export interface GovFeedDetail {
  group: GovFeedRow & {
    aiError: string | null; aiModel: string | null; aiAt: string | null;
    excludeReason: string | null; reviewedBy: string | null; reviewedAt: string | null; latestItemId: string | null;
  };
  items: GovFeedItem[];
  ai: GovFeedAiResult | null;
  /** govProgramFormFromRow(prefill as GovProgram) 로 바로 폼에 넣을 수 있는 모양 */
  prefill: Partial<GovProgram> & { name: string };
  prefillMode: 'asIs' | 'nextYear';
  prefillOrig: { apply_start: string | null; deadline: string | null } | null;
  prefillShiftYears: number;
  dupProgram: { id: string; name: string; deadline: string | null; isActive: boolean } | null;
  /** 사용자가 고친 등록값(부분 payload) — 있으면 prefill 위에 덮는다 */
  draft?: Partial<GovProgram> | null;
  /** 같은 공고(다른 채널) — 대표에 묶인 공고들 (2026-10-03) */
  sameNotices?: GovFeedSameNotice[];
}

/** 엑셀(기업마당 목록 다운로드) 한 행 — 날짜 칸은 문자열·엑셀 일련번호 그대로 보낸다 */
export interface GovFeedImportRow {
  no: string | number; ministry: string; agency: string; field: string; title: string;
  start: string | number | null; end: string | number | null; registered: string | number | null; url: string;
}

/* ── AI 판정 점검 (2026-10-08 · GET/POST /gov-targets-check — backend services/govTargetsCheck.js) ── */
export type GovCheckKind = 'gold' | 'sample' | 'corpus';   // corpus = 수집 공고 시험(로움에 없는 네이버 수집 공고 · 저장 안 함)
export type GovCheckStatus = 'running' | 'done' | 'error';
/** 필수 자격 한 줄 — k: have(보유) · gain(신청 전 취득) · f: 업체 사실 이름 */
export interface GovCheckReq { t: string; k: 'have' | 'gain'; f: string[]; ev?: string }
export interface GovCheckCond {
  emp?: { op: string; v: number } | null; sales?: { op: string; v: number } | null;
  yrs?: { min: number | null; max: number | null } | null; scale?: string;
  age?: { min: number | null; max: number | null } | null;   // 대표자 만 나이 (2026-10-08i)
}
export interface GovCheckAmount { text: string; max: number | null; per: boolean }
/** 지금 등록값 */
export interface GovCheckCurrent {
  category: string; item: string; biz: string[]; subsOn: string[]; subs: string[]; tech: boolean; bizx: string[]; sec: string[];
  req: string[]; reqItems: GovCheckReq[]; cond: GovCheckCond; amount: string; amountMax: number | null; canon: string;
}
/** AI 가 읽은 세부 유형 한 줄 — 등록된 유형이면 칸 · 지원형태 · 대상만, 새로 나눈 유형(isNew)이면 한도(만원)도 (2026-10-08) */
export interface GovCheckAiTrack { name: string; category: string; amountMax: number | null; biz: string[]; subs: string[]; form: '' | 'consult'; isNew: boolean }
/** AI 답 (저장 안 함) */
export interface GovCheckAi {
  item: string; biz: string[]; subs: string[]; subKeys: string[]; any: string[][]; outside: string[]; tech: boolean; bizx: string[]; sec: string[];
  req: GovCheckReq[]; cond: GovCheckCond | null; amount: GovCheckAmount | null; canon: string; confidence: GovAiConf; note: string; dropped: string[];
  evidence: { k: string; q: string }[];
  /* 2026-10-08 지역 「광역/시군구·시군구」 · 지원형태(컨설팅만) · 종류(agency_recruit · umbrella · amendment) · 정책자금 방식 · 세부 유형 — 이전 작업 기록(최근 5회)엔 없을 수 있다 */
  region?: string; form?: '' | 'consult'; kind?: string; loan?: { type: string; use: string } | null; tracks?: GovCheckAiTrack[];
}
export interface GovCheckItem {
  n: number; id: string | null; name: string; link: string; agency: string; deadline: string | null; firstCome: boolean; briefName: string;
  bodyChars: number | null; current: GovCheckCurrent | null; ai: GovCheckAi | null; err: string; notSent?: boolean;
  /* 시험 세트만 — caseCorpus 는 수집 공고 사례(2026-10-08 · id 는 수집 id · current 는 null · 원문은 target) */
  caseName?: string; caseId?: string | null; caseLink?: string | null; caseCorpus?: string | null; expect?: Record<string, unknown>;
  ok?: boolean | null; soft?: boolean; bad?: string[]; missing?: boolean;
  /* 수집 공고 시험만(시험 세트의 수집 사례도 source · target) — id 는 수집 id(예: SME_CONNECT_123) · current 는 null */
  source?: string; target?: string; flags?: GovCheckFlag[];
}
/** 수집 공고 시험의 자동 규칙 검사 결과 (backend services/govTargetsLint.js) — region: 원문에 소재지 조건이 있는데 지역 없음(2026-10-08) */
export type GovCheckFlagKey = 'dropped' | 'req' | 'biz' | 'item' | 'amount' | 'subs' | 'region' | 'conf' | 'none';
export interface GovCheckFlag { k: GovCheckFlagKey; level: 'fail' | 'warn'; t: string }
export interface GovCheckSummary {
  total: number;
  /* 표본 */ read?: number; errors?: number; notSent?: number; dropped?: number; high?: number; mid?: number; low?: number; diff?: number;
  /* 시험 세트 · 수집 공고(fail) */ fail?: number; soft?: number; missing?: number; passed?: boolean;
  /* 수집 공고 */ warn?: number; clean?: number;
}
export interface GovCheckUsage { calls: number; inTok: number; outTok: number; usd: number }
export interface GovCheckRunSummary {
  id: string; kind: GovCheckKind; n: number; model: string; tv: string; status: GovCheckStatus;
  startedAt: string; updatedAt: string; finishedAt: string | null; progress: { done: number; total: number };
  summary: GovCheckSummary | null; usage: GovCheckUsage | null; error: string | null; by?: string; itemCount: number;
}
export interface GovCheckRun extends GovCheckRunSummary { items: GovCheckItem[]; text: string }
export interface GovGoldState { tv: string; at: string; total: number; fail: number; soft: number }
export interface GovCheckInfo {
  runs: GovCheckRunSummary[]; tv: string; goldState: GovGoldState | null; goldCases: number; corpusCount: number; corpusMax: number; active: string | null;
  perItemKrw: number; nDefault: number; nMax: number; model: string; aiConfigured: boolean; budget: { remainingKrw: number | null };
}
